import type { BadgeTone } from '@shared/ui';

import {
    CATEGORY_LABEL,
    CATEGORY_TONE,
    UNSETTLED_CATEGORIES,
    type FinalCategory,
} from './final-category';
import { durationText, formatWhen } from './report-values';

import type { FinalRecord, FinalReport, FinalReportRow } from './final-report';
import type { LabelledValue } from './module-report-types';

/**
 * How the final report is laid out: one SHEET per launched client × record
 * (problems first), then a compact list of the clients that were not
 * migrated, grouped by why. Pure, so the order an auditor reads in is tested.
 */

/**
 * The launched clients that get a sheet, in reading order and split into the
 * sections they are read under: what needs a look, what has not finished, what
 * migrated. A blocked or failed client is never filed under "Migrated".
 */
const SHEET_SECTIONS = [
    {
        key: 'attention',
        title: 'Needs attention',
        categories: ['failed', 'blocked', 'refresh_failed'],
    },
    { key: 'in-progress', title: 'In progress', categories: ['running', 'pending_refresh'] },
    { key: 'migrated', title: 'Migrated', categories: ['migrated_with_warnings', 'migrated'] },
] as const satisfies readonly {
    key: string;
    title: string;
    categories: readonly FinalCategory[];
}[];

const SHEET_ORDER = new Map<FinalCategory, number>(
    SHEET_SECTIONS.flatMap((section) => section.categories).map((category, index) => [
        category,
        index,
    ]),
);

export interface ClientSheet {
    readonly key: string;
    readonly row: FinalReportRow;
    /** `null` for a launched client with no record to show (it still gets its sheet). */
    readonly record: FinalRecord | null;
    /** The first sheet of its client carries the client-level callout. */
    readonly first: boolean;
}

function sheetsOf(rows: readonly FinalReportRow[]): ClientSheet[] {
    return [...rows]
        .sort(
            (a, b) =>
                (SHEET_ORDER.get(a.category) ?? 0) - (SHEET_ORDER.get(b.category) ?? 0) ||
                a.tenant.name.localeCompare(b.tenant.name),
        )
        .flatMap((row) => {
            const records: (FinalRecord | null)[] =
                row.records.length > 0 ? [...row.records] : [null];
            return records.map((record, index) => ({
                key: `${row.tenant.tenantId}:${record?.sourceId ?? 'none'}`,
                row,
                record,
                first: index === 0,
            }));
        });
}

/** Every sheet, in reading order. */
export function clientSheets(report: Pick<FinalReport, 'rows'>): ClientSheet[] {
    return sheetsOf(report.rows.filter((row) => SHEET_ORDER.has(row.category)));
}

export interface SheetSection {
    readonly key: string;
    readonly title: string;
    /** Clients in the section (a client with several records has several sheets). */
    readonly clients: number;
    readonly sheets: readonly ClientSheet[];
}

/** The non-empty sheet sections, in reading order. */
export function sheetSections(report: Pick<FinalReport, 'rows'>): SheetSection[] {
    return SHEET_SECTIONS.map((section) => {
        const categories: readonly FinalCategory[] = section.categories;
        const rows = report.rows.filter((row) => categories.includes(row.category));
        return {
            key: section.key,
            title: section.title,
            clients: rows.length,
            sheets: sheetsOf(rows),
        };
    }).filter((section) => section.clients > 0);
}

export type NotMigratedReason = 'not_selected' | 'not_migratable' | 'skipped' | 'error';

const NOT_MIGRATED_TITLE: Readonly<Record<NotMigratedReason, string>> = {
    not_selected: 'Not selected',
    not_migratable: 'Not migratable',
    skipped: 'Skipped',
    // Not only Salesforce reads: a start that failed, or a run nobody can follow.
    error: 'Errors',
};

const NOT_MIGRATED_ORDER: readonly NotMigratedReason[] = [
    'not_selected',
    'not_migratable',
    'skipped',
    'error',
];

export interface NotMigratedGroup {
    readonly reason: NotMigratedReason;
    readonly title: string;
    readonly rows: readonly FinalReportRow[];
}

/** Non-empty groups only, each sorted by client name. */
export function notMigratedGroups(report: Pick<FinalReport, 'rows'>): NotMigratedGroup[] {
    return NOT_MIGRATED_ORDER.map((reason) => ({
        reason,
        title: NOT_MIGRATED_TITLE[reason],
        rows: report.rows
            .filter((row) => row.category === reason)
            .sort((a, b) => a.tenant.name.localeCompare(b.tenant.name)),
    })).filter((group) => group.rows.length > 0);
}

/** A warning, a failure or a client-level finding, as a sheet's footer shows it. */
export interface Callout {
    readonly code: string;
    /** "blocking", "non-blocking", "failure"… */
    readonly tag: string;
    readonly message: string;
    readonly tone: BadgeTone;
}

const ROW_CALLOUT: Partial<Record<FinalCategory, { tag: string; tone: BadgeTone }>> = {
    blocked: { tag: 'blocked', tone: 'warning' },
    refresh_failed: { tag: 'status unreadable', tone: 'warning' },
    running: { tag: 'in progress', tone: 'info' },
    pending_refresh: { tag: 'awaiting refresh', tone: 'info' },
};

function issueTag(blocking: boolean | null): string {
    if (blocking === true) return 'blocking';
    return blocking === false ? 'non-blocking' : 'warning';
}

/** Failure first, then warnings, then why the client is not settled (once per client). */
export function sheetCallouts(sheet: ClientSheet): Callout[] {
    const callouts: Callout[] = [];
    const tracking = sheet.record?.tracking ?? null;
    if (tracking?.failure) {
        const { code, message } = tracking.failure;
        callouts.push({ code, message, tag: 'failure', tone: 'danger' });
    }
    for (const warning of tracking?.warnings ?? []) {
        callouts.push({
            code: warning.code,
            message: warning.message,
            tag: issueTag(warning.blocking),
            tone: warning.blocking === true ? 'danger' : 'warning',
        });
    }
    if (sheet.record?.notMigrated) {
        callouts.push({ ...sheet.record.notMigrated, tag: 'not migrated', tone: 'neutral' });
    }
    const rowCallout = ROW_CALLOUT[sheet.row.category];
    if (sheet.first && rowCallout !== undefined && sheet.row.message !== null) {
        callouts.push({
            code: sheet.row.reason ?? CATEGORY_LABEL[sheet.row.category],
            message: sheet.row.message,
            ...rowCallout,
        });
    }
    return callouts;
}

/** The record's own status chip: what tracking says, else the client's category. */
export function recordStatus(sheet: ClientSheet): { label: string; tone: BadgeTone } {
    const tracking = sheet.record?.tracking ?? null;
    if (tracking === null) {
        return {
            label: CATEGORY_LABEL[sheet.row.category],
            tone: CATEGORY_TONE[sheet.row.category],
        };
    }
    if (tracking.failure !== null) return { label: tracking.status, tone: 'danger' };
    if (tracking.status === 'Migrated') {
        return { label: 'Migrated', tone: tracking.warnings.length > 0 ? 'warning' : 'success' };
    }
    return { label: tracking.status, tone: tracking.status === 'Error' ? 'danger' : 'neutral' };
}

export interface RunTotalCell {
    readonly label: string;
    /** `null` when no refresh has read the run's totals. */
    readonly value: number | null;
}

/**
 * The run's four totals. The runner's `skipped` is the remainder, so its label
 * follows the client: still pending while unsettled, never run when blocked.
 */
function remainderLabel(category: FinalCategory): string {
    if (UNSETTLED_CATEGORIES.has(category)) return 'Pending';
    return category === 'blocked' ? 'Not run' : 'Skipped';
}

export function runTotalCells(row: FinalReportRow): RunTotalCell[] {
    const totals = row.run?.totals ?? null;
    const keys = [
        ['discovered', 'Discovered'],
        ['migrated', 'Migrated'],
        ['failed', 'Failed'],
        ['skipped', remainderLabel(row.category)],
    ] as const;
    return keys.map(([key, label]) => ({
        label,
        value: totals === null ? null : (totals[key] ?? 0),
    }));
}

/** The sheet's "Run" list; the module's own facts are appended by the caller. */
export function runFacts(sheet: ClientSheet): LabelledValue[] {
    const { run } = sheet.row;
    const tracking = sheet.record?.tracking ?? null;
    const duration = durationText(run?.startedAt ?? null, run?.finishedAt ?? null);
    const status = run?.status ?? CATEGORY_LABEL[sheet.row.category];
    const facts: (LabelledValue | null)[] = [
        { label: 'Status', value: duration === null ? status : `${status} · ${duration}` },
        run?.startedAt ? { label: 'Started', value: formatWhen(run.startedAt) } : null,
        tracking?.migratedAt
            ? { label: 'Migrated at', value: formatWhen(tracking.migratedAt) }
            : null,
        run?.runId ? { label: 'Run ID', value: run.runId, mono: true } : null,
        tracking?.v2Id ? { label: 'V2 ID', value: tracking.v2Id, mono: true } : null,
    ];
    return facts.filter((fact): fact is LabelledValue => fact !== null);
}
